'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Search } from 'lucide-react'
import { AppError } from '@/lib/errors/appError'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTrackByReference } from '@/features/public/hooks/use-public'
import {
  trackByReferenceSchema,
  type TrackByReferenceFormValues,
} from '@/features/public/schemas/track-by-reference.schema'

/**
 * For an investor who came through the public funnel and left the `/status/:trackingToken` page
 * without bookmarking it — the only way back in otherwise (`api-design.md` §4.2). Looks up by
 * Reference ID + the mobile number given for that specific case; any mismatch shows the same
 * generic error (never reveals which field was wrong, matching the backend's own enumeration
 * resistance). On a match, navigates to the existing `/status/:trackingToken` page — this dialog
 * has no display logic of its own.
 */
export function TrackMyStatusDialog() {
  const [open, setOpen] = useState(false)
  const trackByReference = useTrackByReference()
  const form = useForm<TrackByReferenceFormValues>({
    resolver: zodResolver(trackByReferenceSchema),
    mode: 'onTouched',
    defaultValues: { reference: '', mobile: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const result = await trackByReference.mutateAsync(values)
      window.location.href = `/status/${result.trackingToken}`
    } catch {
      // Surfaced inline below via trackByReference.isError.
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          form.reset()
          trackByReference.reset()
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground text-xs font-medium underline underline-offset-2"
            >
              Track My Status
            </button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Already took a service? Track your status here.</TooltipContent>
      </Tooltip>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Track My Status</DialogTitle>
          <DialogDescription>
            Enter the Reference ID and mobile number you gave when you booked.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="track-reference">Reference ID</FieldLabel>
              <Input
                id="track-reference"
                placeholder="e.g. NVX-2026-0001"
                autoCapitalize="characters"
                {...form.register('reference')}
              />
              <FieldError errors={[form.formState.errors.reference]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="track-mobile">Mobile number</FieldLabel>
              <Input
                id="track-mobile"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                {...form.register('mobile', {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                  },
                })}
              />
              <FieldError errors={[form.formState.errors.mobile]} />
            </Field>
          </FieldGroup>
          {trackByReference.isError && (
            <p className="text-destructive mt-3 text-xs">
              {trackByReference.error instanceof AppError &&
              trackByReference.error.code === 'RATE_LIMITED'
                ? 'Too many attempts. Please wait a few minutes and try again.'
                : "We couldn't find a matching request — please check your Reference ID and mobile number."}
            </p>
          )}
          <DialogFooter className="mt-4">
            <Button type="submit" disabled={trackByReference.isPending} className="w-full">
              <Search className="size-4" />
              {trackByReference.isPending ? 'Checking…' : 'Track Status'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
